import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import Table from '@risksmart-app/components/src/table';
import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import type { FC } from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import ExportButton from 'src/components/export-button';
import { Permission } from 'src/rbac/Permission';

import CustomisableRibbon from '@/components/customisable-ribbon/CustomisableRibbon';
import { useGetThirdPartyRegister } from '@/hooks/queries';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useRibbonAndExport } from '@/hooks/useRibbonAndExport';
import { getCounter } from '@/utils/collectionUtils';
import { addThirdPartyUrl } from '@/utils/urls';

import { PageLayout } from '../../layouts';
import { useGetCollectionTableProps } from './config';
import { ThirdPartyRegisterToolbar } from './ThirdPartyRegisterToolbar';
import { useGetDefaultRibbonFilters } from './useGetDefaultRibbonFilters';

const Page: FC = () => {
  const { t: st } = useTranslation(['common'], {
    keyPrefix: 'third_party',
  });
  const title = st('register_title');
  const objectToolbarEnabled = useIsFeatureFlagEnabled('object_toolbar');
  const navigate = useNavigate();
  const openCreateThirdParty = useCallback(() => {
    navigate(addThirdPartyUrl());
  }, [navigate]);

  const { data, loading } = useGetThirdPartyRegister({ queryArgs: {} });

  const tableProps = useGetCollectionTableProps(data?.third_party);
  const counter = getCounter(tableProps.totalItemsCount, loading);

  const { ribbonProps, ribbonExportProps } = useRibbonAndExport(
    useGetDefaultRibbonFilters
  );

  return (
    <PageLayout
      helpTranslationKey={'third_party.registerHelp'}
      title={title}
      counter={counter}
      // Object-level toolbar (CUS-169) — flag-gated. Mounted only when the flag
      // is on, so `useExportOptions` runs on exactly one path (toolbar vs the
      // legacy ExportButton) rather than both.
      headerOverride={
        objectToolbarEnabled ? (
          <ThirdPartyRegisterToolbar
            title={title}
            counter={counter}
            tableProps={tableProps}
            getActiveRibbonFilters={ribbonExportProps.getActiveRibbonFilters}
            onCreate={openCreateThirdParty}
          />
        ) : undefined
      }
      actions={
        <SpaceBetween direction={'horizontal'} size={'xs'}>
          <ExportButton
            tableProps={tableProps}
            entityLabel={title}
            {...ribbonExportProps}
          />
          <Permission permission={'insert:third_party'}>
            <Button variant={'primary'} href={addThirdPartyUrl()}>
              {st('create_new_button')}
            </Button>
          </Permission>
        </SpaceBetween>
      }
    >
      <CustomisableRibbon
        items={tableProps.allItems}
        loading={loading}
        propertyFilterQuery={tableProps.propertyFilterQuery}
        onFilterQueryChanged={tableProps.actions.setPropertyFiltering}
        filteringProperties={tableProps.filteringProperties}
        filteringOptions={tableProps.propertyFilterProps.filteringOptions}
        parentType={Parent_Type_Enum.ThirdParty}
        {...ribbonProps}
        relocateEditToToolbar={objectToolbarEnabled}
      />
      <Table {...tableProps} loading={loading} />
    </PageLayout>
  );
};

export default Page;
